import { ActionTree } from "vuex";
import {
  ILoyaltyState,
  ISegmentOfferResponse,
  IAccountOffer,
  IAccountOfferResponse,
} from "./types";
import { IRootState } from "@/store/types";
import { LoyaltySchemeType, EarnRateType } from "@/store/loyalty/types";
import { LOYALTY_MUTATIONS } from "./mutations";
import { UI } from "@/store/ui";
import api from "@/api/loyalty/api";
import { LOYALTY } from ".";

export namespace ERROR_MESSAGES {
  export const LOAD_LOYALTY_LIST_ERROR =
    "There was a problem trying to load the loyalty segment offers. Please try again later.";
  export const LOAD_CHANNEL_LIST_ERROR =
    "There was a problem trying to load the channel reference data. Please try again later.";
  export const LOAD_SEGMENT_LIST_ERROR =
    "There was a problem trying to load the segment reference data. Please try again later.";
  export const LOAD_SEGMENT_OFFER_ERROR =
    "There was a problem trying to load the loyalty segment offer. Please try again later.";
  export const UPDATE_SEGMENT_OFFER_ERROR =
    "There was a problem trying to update the loyalty segment offer. Please try again later.";
  export const LOAD_ACCOUNT_OFFER_LIST_ERROR =
    "There was a problem trying to load the loyalty account offers. Please try again later.";
  export const LOAD_ACCOUNT_OFFER_ERROR =
    "There was a problem trying to load the loyalty account offer. Please try again later.";
  export const UPDATE_ACCOUNT_OFFER_ERROR =
    "There was a problem trying to update the loyalty account offer. Please try again later.";
  export const DELETE_ACCOUNT_OFFER_ERROR =
    "There was a problem trying to delete the loyalty account offer. Please try again later.";
  export const SEARCH_CUSTOMER_LOOKUP_ERROR =
    "There was a problem trying to searching for customers. Please try again later.";
  export const LOAD_CUSTOMER_LIST_ERROR =
    "There was a problem trying to load the customers. Please try again later.";
  export const LOAD_CUSTOMER_ERROR =
    "There was a problem trying to load the customer data. Please try again later.";
  export const LOAD_CUSTOMER_CARDS_ERROR =
    "There was a problem trying to load the customer cards data. Please try again later.";
  export const UPDATE_CARD_LOYALTY_ASSOCIATION_ERROR =
    "There was a problem trying to update the card loyalty association. Please try again later.";
  export const UPDATE_COST_CENTRE_LOYALTY_ASSOCIATION_ERROR =
    "There was a problem trying to update the cost centre loyalty association. Please try again later.";
  export const UPDATE_ACCOUNT_LOYALTY_ASSOCIATION_ERROR =
    "There was a problem trying to update the account loyalty association. Please try again later.";
  export const LOAD_CUSTOMER_TRANSACTION_LIST_ERROR =
    "There was a problem trying to load the transactions. Please try again later.";
  export const LOAD_CUSTOMER_POINTS_LIST_ERROR =
    "There was a problem trying to retrieve the points. Please try again later.";
  export const LOAD_CAMPAIGN =
    "There was a problem trying to retrieve the campaign. Please try again later.";
  export const LOAD_CAMPAIGN_LIST =
    "There was a problem trying to retrieve the campaigns list. Please try again later.";
  export const SAVE_CAMPAIGN_ERROR =
    "There was a problem trying to save the campaign. Please try again later.";
}

export namespace LOYALTY_ACTIONS {
  export const TOGGLE_IS_LOADING = "toggleIsLoading";
  export const SET_SEGMENT_OFFER = "setSegmentOffer";
  export const LOAD_SEGMENT_OFFER_LIST = "loadLoyaltyList";
  export const LOAD_SEGMENT_OFFER = "loadSegmentOffer";
  export const LOAD_CHANNEL_REFERENCE_DATA = "loadChannelReferenceData";
  export const LOAD_SEGMENT_REFERENCE_DATA = "loadSegmentReferenceData";
  export const SET_SEGMENT_OFFER_SEGMENT = "setSegmentOfferSegment";
  export const UPDATE_SEGMENT_OFFER = "updateSegmentOffer";
  export const SET_ACCOUNT_OFFER = "setAccountOffer";
  export const LOAD_ACCOUNT_OFFER = "loadAccountOffer";
  export const LOAD_ACCOUNT_OFFER_LIST = "loadAccountOfferList";
  export const SET_ACCOUNT_OFFER_CUSTOMER = "setAccountOfferCustomer";
  export const SEARCH_CUSTOMER_LOOKUP = "searchCustomerLookup";
  export const UPDATE_ACCOUNT_OFFER = "updateAccountOffer";
  export const DELETE_ACCOUNT_OFFER = "deleteAccountOffer";
  export const LOAD_CUSTOMER_LIST = "loadCustomers";
  export const SET_CUSTOMER = "setCustomer";
  export const LOAD_CUSTOMER = "loadCustomer";
  export const SET_CUSTOMER_CARDS = "setCustomerCards";
  export const LOAD_CUSTOMER_CARDS = "loadCustomerCards";
  export const LOAD_CUSTOMER_POINTS = "loadCustomerPoints";
  export const LOAD_CUSTOMER_TRANSACTIONS = "loadCustomerTransactions";
  export const UPDATE_CARD_LOYALTY_ASSOCIATION = "updateCardLoyaltyAssociation";
  export const UPDATE_COST_CENTRE_LOYALTY_ASSOCIATION =
    "updateCostCentreLoyaltyAssociation";
  export const UPDATE_ACCOUNT_LOYALTY_ASSOCIATION =
    "updateAccountLoyaltyAssociation";
  export const SET_ACTIVE_CUSTOMER_CARD = "setActiveCustomerCard";
  export const SET_CAMPAIGN = "setCampaign";
  export const LOAD_CAMPAIGN = "loadCampaign";
  export const LOAD_CAMPAIGN_LIST = "loadCampaignList";
  export const CREATE_CAMPAIGN = "createCampaign";
  export const UPDATE_CAMPAIGN = "updateCampaign";
}

const INVALID_API_VALIDATION_CODE = 400;

function mapAccountOfferStateToPost(accountOffer: IAccountOffer) {
  return {
    id: accountOffer.id,
    level: accountOffer.level,
    isEligible: accountOffer.isEligible,
    channels: accountOffer.channels,
    accountNumber: accountOffer.accountNumber,
    customerName: accountOffer.customerName,
    applicationNumber: accountOffer.applicationNumber,
    contactName: accountOffer.contactName,
    contactTitle: accountOffer.contactTitle,
    segment: accountOffer.segment,
    customerType: accountOffer.customerType,
    emailAddress: accountOffer.emailAddress,
    earnRates: [
      {
        accumulationQuantity: 1,
        quantity: accountOffer.airpointsEarnRateQuantity,
        earnRateUnit: EarnRateType.Litres,
        loyaltyScheme: LoyaltySchemeType.Airpoints,
      },
      {
        accumulationQuantity: 1,
        quantity: accountOffer.flyBuysEarnRateQuantity,
        earnRateUnit: EarnRateType.Litres,
        loyaltyScheme: LoyaltySchemeType.FlyBuys,
      },
    ],
  } as IAccountOfferResponse;
}

export const actions: ActionTree<ILoyaltyState, IRootState> = {
  [LOYALTY_ACTIONS.TOGGLE_IS_LOADING]({ commit }, isLoading): void {
    commit(LOYALTY_MUTATIONS.TOGGLE_IS_LOADING, isLoading);
  },
  async [LOYALTY_ACTIONS.LOAD_CHANNEL_REFERENCE_DATA]({
    commit,
    dispatch,
  }): Promise<void> {
    try {
      const response: any = await api.getChannels();
      commit(LOYALTY_MUTATIONS.SET_CHANNEL_REFERENCE_DATA, response.data.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-channels",
          text: ERROR_MESSAGES.LOAD_CHANNEL_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  async [LOYALTY_ACTIONS.LOAD_SEGMENT_REFERENCE_DATA]({
    commit,
    dispatch,
  }): Promise<void> {
    try {
      const response: any = await api.getSegments();
      commit(LOYALTY_MUTATIONS.SET_SEGMENT_REFERENCE_DATA, response.data.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-segments",
          text: ERROR_MESSAGES.LOAD_SEGMENT_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  async [LOYALTY_ACTIONS.LOAD_SEGMENT_OFFER_LIST]({
    commit,
    dispatch,
  }): Promise<void> {
    try {
      const response: any = await api.getSegmentOffers();
      commit(LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_LIST, response.data.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-segment-offers",
          text: ERROR_MESSAGES.LOAD_LOYALTY_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  [LOYALTY_ACTIONS.SET_SEGMENT_OFFER]({ commit }, segmentOffer): void {
    commit(LOYALTY_MUTATIONS.SET_SEGMENT_OFFER, segmentOffer);
  },
  async [LOYALTY_ACTIONS.LOAD_SEGMENT_OFFER](
    { commit, dispatch },
    segmentOfferId
  ): Promise<void> {
    try {
      const response: any = await api.getSegmentOffer(segmentOfferId);
      const segmentOfferResponse = response.data as ISegmentOfferResponse;

      commit(LOYALTY_MUTATIONS.SET_SEGMENT_OFFER, segmentOfferResponse);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-segment-offer",
          text: ERROR_MESSAGES.LOAD_SEGMENT_OFFER_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  async [LOYALTY_ACTIONS.UPDATE_SEGMENT_OFFER](
    { dispatch, state },
    segmentOfferId
  ) {
    const segmentOffer = state.segmentOffer;
    let success = false;

    // map " flattened" segmentOffer state to the shape the API consumes
    const segmentOfferData = {
      id: segmentOffer.id,
      segment: segmentOffer.segment,
      isEligible: segmentOffer.isEligible,
      channels: segmentOffer.channels,
      level: segmentOffer.level,
      earnRates: [
        {
          accumulationQuantity: 1,
          quantity: segmentOffer.airpointsEarnRateQuantity,
          earnRateUnit: EarnRateType.Litres,
          loyaltyScheme: LoyaltySchemeType.Airpoints,
        },
        {
          accumulationQuantity: 1,
          quantity: segmentOffer.flyBuysEarnRateQuantity,
          earnRateUnit: EarnRateType.Litres,
          loyaltyScheme: LoyaltySchemeType.FlyBuys,
        },
      ],
    } as ISegmentOfferResponse;

    try {
      await api.putSegmentOffer(segmentOfferId, segmentOfferData);
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-update-segment-offer",
          text: ERROR_MESSAGES.UPDATE_SEGMENT_OFFER_ERROR,
          error,
        },
        { root: true }
      );
    }

    dispatch(
      UI.NS(UI.ACTIONS.ADD_TOAST),
      {
        id: "updated-segment-offer",
        text: "Successfully updated segment offer",
      },
      { root: true }
    );

    return success;
  },
  [LOYALTY_ACTIONS.SET_SEGMENT_OFFER_SEGMENT]({ commit }, segment): void {
    commit(LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_SEGMENT, segment);
  },
  async [LOYALTY_ACTIONS.LOAD_ACCOUNT_OFFER_LIST](
    { commit, dispatch },
    { pagination }
  ): Promise<void> {
    try {
      const response: any = await api.getAccountOffers(pagination);

      commit(LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_LIST, response.data.data);
      commit(
        LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_LIST_PAGINATION,
        response.data.pagination
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-segment-offers",
          text: ERROR_MESSAGES.LOAD_ACCOUNT_OFFER_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  [LOYALTY_ACTIONS.SET_ACCOUNT_OFFER]({ commit }, accountOffer): void {
    commit(LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER, accountOffer);
  },
  async [LOYALTY_ACTIONS.LOAD_ACCOUNT_OFFER](
    { commit, dispatch },
    accountNumber
  ): Promise<void> {
    try {
      const response: any = await api.getAccountOffer(accountNumber);
      commit(LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER, response.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-account-offer",
          text: ERROR_MESSAGES.LOAD_ACCOUNT_OFFER_ERROR,
          error,
        },
        { root: true }
      );
    }
  },

  async [LOYALTY_ACTIONS.SEARCH_CUSTOMER_LOOKUP](
    { commit, dispatch },
    searchTerm
  ): Promise<void> {
    if (!searchTerm) {
      return;
    }

    commit(LOYALTY.MUTATIONS.SET_CUSTOMER_SEARCH_IN_PROGRESS, true);
    try {
      const response = await api.searchCustomer(searchTerm);
      commit(LOYALTY.MUTATIONS.SET_CUSTOMER_SEARCH_LIST, response.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-search-customer-lookup",
          text: ERROR_MESSAGES.SEARCH_CUSTOMER_LOOKUP_ERROR,
          error,
        },
        { root: true }
      );
    }

    commit(LOYALTY.MUTATIONS.SET_CUSTOMER_SEARCH_IN_PROGRESS, false);
  },
  [LOYALTY_ACTIONS.SET_ACCOUNT_OFFER_CUSTOMER]({ commit }, customer): void {
    commit(LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_CUSTOMER, customer);
  },

  async [LOYALTY_ACTIONS.UPDATE_ACCOUNT_OFFER]({
    dispatch,
    state,
  }): Promise<boolean> {
    let success = false;
    try {
      const accountOfferPost = mapAccountOfferStateToPost(state.accountOffer);
      await api.updateAccountOffer(
        Number(state.accountOffer.accountNumber),
        accountOfferPost
      );

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "updated-account-offer",
          text: "Successfully updated account offer",
        },
        { root: true }
      );
      success = true;
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-update-account-offer",
          text: ERROR_MESSAGES.UPDATE_ACCOUNT_OFFER_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [LOYALTY_ACTIONS.DELETE_ACCOUNT_OFFER]({
    dispatch,
    state,
  }): Promise<boolean> {
    let success = false;
    try {
      await api.deleteAccountOffer(Number(state.accountOffer.accountNumber));
      success = true;

      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "deleted-account-offer",
          text: "Successfully deleted account offer",
        },
        { root: true }
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-delete-account-offer",
          text: ERROR_MESSAGES.DELETE_ACCOUNT_OFFER_ERROR,
          error,
        },
        { root: true }
      );
    }
    return success;
  },
  async [LOYALTY_ACTIONS.LOAD_CUSTOMER_LIST](
    { commit, dispatch },
    { pagination }
  ): Promise<void> {
    try {
      const response: any = await api.getCustomers(pagination);

      commit(LOYALTY_MUTATIONS.SET_CUSTOMER_LIST, response.data.data);
      commit(
        LOYALTY_MUTATIONS.SET_CUSTOMER_LIST_PAGINATION,
        response.data.pagination
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-customer-list",
          text: ERROR_MESSAGES.LOAD_CUSTOMER_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  [LOYALTY_ACTIONS.SET_CUSTOMER]({ commit, dispatch }, customer): void {
    commit(LOYALTY_MUTATIONS.SET_CUSTOMER, customer);
  },
  async [LOYALTY_ACTIONS.LOAD_CUSTOMER](
    { commit, dispatch },
    customerId
  ): Promise<void> {
    try {
      const response: any = await api.getCustomer(customerId);
      commit(LOYALTY_MUTATIONS.SET_CUSTOMER, response.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-customer",
          text: ERROR_MESSAGES.LOAD_CUSTOMER_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  [LOYALTY_ACTIONS.SET_CUSTOMER_CARDS]({ commit }, customerList): void {
    commit(LOYALTY_MUTATIONS.SET_CUSTOMER_CARDS, customerList);
  },
  async [LOYALTY_ACTIONS.LOAD_CUSTOMER_CARDS](
    { commit, dispatch },
    { customerId }
  ): Promise<void> {
    try {
      commit(LOYALTY_MUTATIONS.TOGGLE_IS_LOADING, true);
      const response: any = await api.getCustomerCards(
        customerId,
        this.state.loyalty.dataTables.customerCards.pagination
      );
      commit(LOYALTY_MUTATIONS.TOGGLE_IS_LOADING, false);
      commit(LOYALTY_MUTATIONS.SET_CUSTOMER_CARDS, response.data.data);
      commit(
        LOYALTY_MUTATIONS.SET_CUSTOMER_CARDS_LIST_PAGINATION,
        response.data.pagination
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-customer-cards",
          text: ERROR_MESSAGES.LOAD_CUSTOMER_CARDS_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  [LOYALTY_ACTIONS.SET_ACTIVE_CUSTOMER_CARD]({ commit }, value): void {
    commit(LOYALTY_MUTATIONS.SET_ACTIVE_CUSTOMER_CARD, value);
  },
  async [LOYALTY_ACTIONS.UPDATE_CARD_LOYALTY_ASSOCIATION](
    { dispatch },
    {
      accountNumber,
      cardNumber,
      loyaltyNumber,
      loyaltyScheme,
      airpointsFirstName,
      airpointsLastName,
    }
  ): Promise<any> {
    let validationFailed = false;
    let success = false;
    try {
      await api.putCardLoyaltyAssociation({
        accountNumber,
        loyaltyNumber,
        loyaltyScheme,
        cardNumber,
        airpointsFirstName,
        airpointsLastName,
      });
      success = true;
    } catch (error: any) {
      // 404 means there was a validation error
      if (error.response.status !== INVALID_API_VALIDATION_CODE) {
        dispatch(
          UI.NS(UI.ACTIONS.ADD_TOAST),
          {
            id: "failed-put-loyalty-association",
            text: ERROR_MESSAGES.UPDATE_CARD_LOYALTY_ASSOCIATION_ERROR,
            error,
          },
          { root: true }
        );
      } else {
        validationFailed = true;
      }
    }
    return {
      validationFailed,
      success,
    };
  },
  async [LOYALTY_ACTIONS.UPDATE_COST_CENTRE_LOYALTY_ASSOCIATION](
    { dispatch },
    {
      accountNumber,
      costCentreCode,
      loyaltyNumber,
      loyaltyScheme,
      airpointsFirstName,
      airpointsLastName,
    }
  ): Promise<any> {
    let validationFailed = false;
    let success = false;
    try {
      await api.putCostCentreLoyaltyAssociation({
        accountNumber,
        loyaltyNumber,
        loyaltyScheme,
        costCentreCode,
        airpointsFirstName,
        airpointsLastName,
      });
      success = true;
    } catch (error: any) {
      // 404 means there was a validation error
      if (error.response.status !== INVALID_API_VALIDATION_CODE) {
        dispatch(
          UI.NS(UI.ACTIONS.ADD_TOAST),
          {
            id: "failed-put-loyalty-association",
            text: ERROR_MESSAGES.UPDATE_COST_CENTRE_LOYALTY_ASSOCIATION_ERROR,
            error,
          },
          { root: true }
        );
      } else {
        validationFailed = true;
      }
    }
    return {
      validationFailed,
      success,
    };
  },
  async [LOYALTY_ACTIONS.UPDATE_ACCOUNT_LOYALTY_ASSOCIATION](
    { dispatch },
    {
      accountNumber,
      loyaltyNumber,
      loyaltyScheme,
      airpointsFirstName,
      airpointsLastName,
    }
  ): Promise<any> {
    let validationFailed = false;
    let success = false;
    try {
      await api.putAccountLoyaltyAssociation({
        accountNumber,
        loyaltyNumber,
        loyaltyScheme,
        airpointsFirstName,
        airpointsLastName,
      });
      success = true;
    } catch (error: any) {
      // 404 means there was a validation error
      if (error.response.status !== INVALID_API_VALIDATION_CODE) {
        dispatch(
          UI.NS(UI.ACTIONS.ADD_TOAST),
          {
            id: "failed-put-loyalty-association",
            text: ERROR_MESSAGES.UPDATE_ACCOUNT_LOYALTY_ASSOCIATION_ERROR,
            error,
          },
          { root: true }
        );
      } else {
        validationFailed = true;
      }
    }
    return {
      validationFailed,
      success,
    };
  },
  async [LOYALTY_ACTIONS.LOAD_CUSTOMER_TRANSACTIONS](
    { commit, dispatch },
    { month, year, accountNumber }
  ): Promise<void> {
    try {
      const response: any = await api.getTransactions(
        month,
        year,
        accountNumber
      );
      commit(
        LOYALTY_MUTATIONS.SET_CUSTOMER_TRANSACTION_LIST,
        response.data.data
      );
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-customer-transaction-list",
          text: ERROR_MESSAGES.LOAD_CUSTOMER_TRANSACTION_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  [LOYALTY_ACTIONS.SET_CAMPAIGN]({ commit }, value): void {
    commit(LOYALTY_MUTATIONS.SET_CAMPAIGN, value);
  },
  async [LOYALTY_ACTIONS.LOAD_CAMPAIGN](
    { commit, dispatch },
    campaignId
  ): Promise<void> {
    try {
      const response: any = await api.getCampaign(campaignId);
      commit(LOYALTY_MUTATIONS.SET_CAMPAIGN, response.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-loyalty-campaign",
          text: ERROR_MESSAGES.LOAD_CAMPAIGN,
          error,
        },
        { root: true }
      );
    }
  },
  async [LOYALTY_ACTIONS.LOAD_CUSTOMER_POINTS](
    { commit, dispatch },
    { accountNumber, month, year }
  ): Promise<void> {
    try {
      const response: any = await api.getPoints(accountNumber, year, month);
      commit(LOYALTY_MUTATIONS.SET_CUSTOMER_POINTS_LIST, response.data.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-customer-points-list",
          text: ERROR_MESSAGES.LOAD_CUSTOMER_POINTS_LIST_ERROR,
          error,
        },
        { root: true }
      );
    }
  },
  async [LOYALTY_ACTIONS.LOAD_CAMPAIGN_LIST](
    { commit, dispatch },
    { pagination }
  ): Promise<void> {
    try {
      const response: any = await api.getCampaignList(pagination);
      commit(LOYALTY_MUTATIONS.SET_CAMPAIGN_LIST, response.data.data);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-load-commercial-campaigns",
          text: ERROR_MESSAGES.LOAD_CAMPAIGN_LIST,
          error,
        },
        { root: true }
      );
    }
  },
  async [LOYALTY_ACTIONS.CREATE_CAMPAIGN]({ dispatch }, campaign) {
    try {
      await api.postCampaign(campaign);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-create-campaign",
          text: ERROR_MESSAGES.SAVE_CAMPAIGN_ERROR,
          error,
        },
        { root: true }
      );

      return false;
    }

    dispatch(
      UI.NS(UI.ACTIONS.ADD_TOAST),
      {
        id: "created-campaign",
        text: "Successfully created campaign",
      },
      { root: true }
    );

    return true;
  },
  async [LOYALTY_ACTIONS.UPDATE_CAMPAIGN]({ dispatch }, { id, campaign }) {
    try {
      await api.putCampaign(id, campaign);
    } catch (error: any) {
      dispatch(
        UI.NS(UI.ACTIONS.ADD_TOAST),
        {
          id: "failed-update-campaign",
          text: ERROR_MESSAGES.SAVE_CAMPAIGN_ERROR,
          error,
        },
        { root: true }
      );
      return false;
    }

    dispatch(
      UI.NS(UI.ACTIONS.ADD_TOAST),
      {
        id: "updated-campaign",
        text: "Successfully updated campaign",
      },
      { root: true }
    );

    return true;
  },
};
